import { routesPermissions } from 'common/auth/permissions'
import {
  Account,
  Campaigns as CampaignsIcon,
  Edit as EditIcon,
  Insights,
  Performance,
  Reports as ReportsIcon,
  Settings,
  Users,
} from 'common/icons'
import Admin from 'features/admin'
import AdminAccountSettings from 'features/admin/accountSettings'
import BrandVoiceRedirect from 'features/admin/brandVoice/BrandVoiceRedirect'
import DataSources from 'features/admin/dataSources'
import AdminRegionSettings from 'features/admin/regions/RegionSettings'
import AdminUsers from 'features/admin/users'
import Campaigns from 'features/campaigns/Campaigns'
import ContentNodes from 'features/content/nodes'
import FuturamaContentsPage from 'features/futurama/contentsPage'
import Personalization from 'features/personalization'
import Projects from 'features/projects'
import ReportsInsights from 'features/reports/insights'
import ReportsPerformance from 'features/reports/performance'
import Reports from 'features/reports/Reports'

import type { StaticRoute, Subroute } from './interfaces'
import { dispatchClickNavItem } from './navigationSlice'
import {
  brandVoicePath,
  createProjectPath,
  dataSourcesPath,
  projectsPath,
} from './paths'
import { demoRoute } from './staticRoutes'
import { accountSettingsSubroutes } from './staticSubRoutes.accountSettings'

const projectsCreateRoute: StaticRoute = {
  key: 'projects-create',
  component: Campaigns,
  path: createProjectPath,
  name: 'project',
  to: createProjectPath,
  icon: CampaignsIcon,
  accessLevel: 'hero',
  accessPermission: routesPermissions.projectsPageView,
  accessFlags: { flags: ['projectScreens'], type: 'hide' },
  shouldValidateAccountAccess: true,
}

const projectSubRoute: Subroute = {
  key: 'projects',
  component: Projects,
  name: 'projects',
  to: projectsPath,
  icon: CampaignsIcon,
  accessPermission: routesPermissions.projectsPageView,
  accessFlags: { flags: ['projectScreens'], type: 'hide' },
  shouldValidateAccountAccess: true,
}

export const selectedCampaignPath =
  '/accounts/:accountId/campaigns/:campaignId?'

export const campaignsRoute: StaticRoute = {
  key: 'campaigns',
  component: Campaigns,
  path: ['/accounts/:accountId/campaigns', selectedCampaignPath],
  name: 'experiments',
  to: '/accounts/:accountId/campaigns',
  icon: CampaignsIcon,
  accessLevel: 'hero',
  accessPermission: routesPermissions.experimentsView,
  shouldValidateAccountAccess: true,
}

const reportsInsightsPath = '/accounts/:accountId/reports/insights'

const reportsPerformancePath = '/accounts/:accountId/reports/performance'

export const reportsRoute: StaticRoute = {
  key: 'reports',
  component: Reports,
  path: ['/reports', '/accounts/:accountId/reports'],
  name: 'reports',
  to: '/accounts/:accountId/reports',
  icon: ReportsIcon,
  accessLevel: 'hero',
  accessPermission: routesPermissions.reportsView,
  shouldValidateAccountAccess: true,
  accessFlags: {
    flags: ['showReportsPage'],
    type: 'disable',
  },
  subroutes: [
    {
      icon: Performance,
      key: 'performance',
      name: 'Performance',
      component: ReportsPerformance,
      to: reportsPerformancePath,
      onClick: dispatchClickNavItem,
      accessFlags: {
        flags: ['showReportsPage'],
        type: 'hide',
      },
    },
    {
      icon: Insights,
      key: 'insights',
      name: 'Insights',
      component: ReportsInsights,
      to: reportsInsightsPath,
      onClick: dispatchClickNavItem,
      accessFlags: {
        flags: ['showReportsPage'],
        type: 'hide',
      },
    },
  ],
}

export const adminUsersSubroute: Subroute = {
  icon: Users,
  key: 'users',
  name: 'users',
  component: AdminUsers,
  to: '/admin/users',
  accessPermission: routesPermissions.usersView,
  onClick: dispatchClickNavItem,
}

export const dataSourcesSubroute: Subroute = {
  icon: Settings,
  key: 'data-sources',
  name: 'Data sources',
  component: DataSources,
  to: dataSourcesPath,
  onClick: dispatchClickNavItem,
  accessFlags: {
    flags: ['showPersonalizationDataImport'],
    type: 'hide',
  },
  accessPermission: routesPermissions.dataSourcesCreate,
}

export const brandVoiceSubroute: Subroute = {
  icon: Settings,
  key: 'brand-voice',
  name: 'Brand Voice',
  component: BrandVoiceRedirect,
  to: brandVoicePath,
  onClick: dispatchClickNavItem,
  accessPermission: routesPermissions.brandVoiceSettingsView,
}

export const adminRegionSettingsRoute: Subroute = {
  icon: Settings,
  key: 'region',
  name: 'region',
  component: AdminRegionSettings,
  to: '/admin/language/region',
  onClick: dispatchClickNavItem,
  accessPermission: routesPermissions.contentView,
}

export const adminSettingsRoute: Subroute = {
  icon: Settings,
  key: 'settings',
  name: 'settings',
  component: AdminAccountSettings,
  to: '/admin/accounts/:accountId?',
  onClick: dispatchClickNavItem,
  accessFlags: {
    flags: ['showAdminAccountSettingsPage'],
    type: 'hide',
  },
  accessPermission: routesPermissions.accountSettingsView,
  tabbedNavRoutes: accountSettingsSubroutes,
  shouldValidateAccountAccess: true,
}

export const futuramaContentRouteForTopNav: StaticRoute = {
  key: 'content',
  path: '/content',
  component: FuturamaContentsPage,
  icon: EditIcon,
  name: 'Content',
  to: '/content',
  accessLevel: 'superhero',
  onClick: dispatchClickNavItem,
  accessFlags: {
    flags: ['showFuturamaPage'],
    type: 'hide',
  },
}

export const personalizationRoute: StaticRoute = {
  key: 'personalization',
  path: '/personalization',
  component: Personalization,
  icon: EditIcon,
  name: 'Personalization',
  to: '/personalization',
  accessLevel: 'superhero',
  onClick: dispatchClickNavItem,
  accessFlags: {
    flags: ['showPersonalizationDataImport'],
    type: 'hide',
  },
}

export const languageSubroute: Subroute = {
  icon: EditIcon,
  key: 'language',
  name: 'language',
  component: ContentNodes,
  to: '/admin/language/region',
  onClick: dispatchClickNavItem,
  accessPermission: routesPermissions.languageView,
}

export const createContentRoutes: StaticRoute[] = [
  campaignsRoute,
  projectsCreateRoute,
  futuramaContentRouteForTopNav,
]

export const adminRoutes: StaticRoute = {
  key: 'admin',
  path: '/admin',
  component: Admin,
  name: 'admin',
  to: '/admin/accounts/:accountId?',
  icon: Account,
  accessLevel: 'superhero',
  accessFlags: {
    flags: ['showAdminPage'],
    type: 'disable',
  },
  subroutes: [
    projectSubRoute,
    brandVoiceSubroute,
    adminUsersSubroute,
    adminSettingsRoute,
    languageSubroute,
    dataSourcesSubroute,
  ],
}

export const topNavStaticRoutes: StaticRoute[] = [
  demoRoute,
  campaignsRoute,
  futuramaContentRouteForTopNav,
  personalizationRoute,
  reportsRoute,
  adminRoutes,
]
